import { FC, memo, startTransition, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  CloseFullscreen,
  GridView,
  Minimize,
  OpenInFull,
  RestartAlt,
  ShowChart,
  UnfoldMore,
} from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { IUserDto, useUser } from "@framework/provider-user";
import { normalizeUserMultiAnalysisSettings } from "@framework/types";

import { useMultiAnalysis } from "../../../../../../context/multi-analysis-context";
import {
  MultilineChartOrbit,
  RotationDirection,
} from "../../../scheme/components/analytics/components/multiline-chart-orbit";
import {
  MultilineChartSignal,
  type ISyncedCursorAction,
} from "../../../scheme/components/analytics/components/multiline-chart-signal";
import type {
  IFaultFrequencyChartCursor,
  TRAIN_ROTATION_DIRECTION,
} from "../../../scheme/components/analytics/interfaces";
import {
  useMultiAnalysisGraphicAreaData,
  type TMultiAnalysisGraphicAreaCellData,
} from "../../hooks/use-multi-analysis-graphic-area-data";
import {
  MULTI_ANALYSIS_MEASURE_TYPE,
  ROTATION_DIRECTION,
  SIGNAL_TIME_SCALE,
  SPECTRUM_FREQUENCY_SCALE,
  SYNC_CURSOR_LINE_COLOR,
  isSyncableMeasureType,
  type IMultiAnalysisTimeRange,
  type IMultiAnalysisToolbarState,
} from "../../types";
import {
  canConvertAnySignalChartUnits,
  convertSignalChartUnits,
  resolveNativeSignalDisplayUnit,
} from "../../utils/signal-unit-conversion";
import { convertClickedSpectrumXToHz } from "../../utils/frequency-profile";
import { convertNativeXToPhysicalX, resolveSyncNativeValues } from "../../utils/sync-cursors";
import { filterSignalChartData } from "../../utils/signal-filter";
import { filterGraphicAreaDataByHiddenMeasureKeys } from "../../utils/trend-measure-list";
import {
  type IMultiAnalysisTemplateCell,
  type INormalizedAnalysisTemplate,
  resolveActiveMeasureWindow,
  resolveAppliedAnalysisTemplate,
} from "../../utils/analysis-template-settings";
import { MultiAnalysisPanel } from "../panel";
import { SignalFilterDialog } from "../signal-filter-dialog";
import { TemplateCellEditor } from "../template-cell-editor";

const GRAPHIC_AREA_BORDER = "1px solid #d8ddea";
const GRAPHIC_AREA_HEADER_MIN_HEIGHT = 38;
const GRAPHIC_AREA_CELL_MIN_HEIGHT = 220;
const GRAPHIC_AREA_GRID_PADDING = 8;
const GRAPHIC_AREA_GRID_GAP = 8;
const GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT = 48;
const GRAPHIC_AREA_CELL_COLLAPSED_WIDTH = 100;

const findRotationFrequencyRpm = (series: readonly { rotationFrequencyRpm?: number | null }[]): number | null =>
  series.find(
    item =>
      item.rotationFrequencyRpm != null && Number.isFinite(item.rotationFrequencyRpm) && item.rotationFrequencyRpm > 0,
  )?.rotationFrequencyRpm ?? null;

export const getMultiAnalysisGraphicAreaMinHeight = (rowCount: number): number => {
  const rows = Math.max(1, Math.ceil(rowCount));

  return (
    GRAPHIC_AREA_HEADER_MIN_HEIGHT +
    GRAPHIC_AREA_GRID_PADDING * 2 +
    rows * GRAPHIC_AREA_CELL_MIN_HEIGHT +
    Math.max(0, rows - 1) * GRAPHIC_AREA_GRID_GAP
  );
};

const MEASURE_WINDOW_LABEL_KEYS: Record<MULTI_ANALYSIS_MEASURE_TYPE, string> = {
  [MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM]: "spectrum",
  [MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM]: "signal",
  [MULTI_ANALYSIS_MEASURE_TYPE.ORBIT]: "orbit",
  [MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND]: "polar",
};

const mapToolbarRotationDirection = (direction: ROTATION_DIRECTION): RotationDirection => {
  switch (direction) {
    case ROTATION_DIRECTION.COUNTER_CLOCKWISE:
      return RotationDirection.COUNTER_CLOCKWISE;
    case ROTATION_DIRECTION.CLOCKWISE:
      return RotationDirection.CLOCKWISE;
    case ROTATION_DIRECTION.UNDEFINED:
    default:
      return RotationDirection.UNDEFINED;
  }
};

interface IMultiAnalysisGraphicAreaProps {
  templates: readonly INormalizedAnalysisTemplate[];
  isLoadingTemplates: boolean;
  trainName: string;
  range: IMultiAnalysisTimeRange;
  appliedTemplateId: number | null;
  hiddenMeasureKeys: ReadonlySet<string>;
  fillAvailableHeight?: boolean;
  faultFrequencies?: IFaultFrequencyChartCursor[] | null;
  trainRotationDirection?: TRAIN_ROTATION_DIRECTION | null;
  onActiveSignalUnitsAvailabilityChange?: (canConvert: boolean) => void;
}

const MeasureWindowCell: FC<{
  cell: IMultiAnalysisTemplateCell;
  isActive: boolean;
  isExpanded: boolean;
  isCollapsed: boolean;
  canToggleExpand: boolean;
  canToggleCollapse: boolean;
  hasSelectedPoints: boolean;
  data: TMultiAnalysisGraphicAreaCellData | null | undefined;
  isLoading: boolean;
  displayCascade3D: boolean;
  fillAvailableHeight: boolean;
  toolbarState: IMultiAnalysisToolbarState;
  setToolbarState: (patch: Partial<IMultiAnalysisToolbarState>) => void;
  onSelect: (cell: IMultiAnalysisTemplateCell) => void;
  onToggleExpand: (cell: IMultiAnalysisTemplateCell) => void;
  onToggleCollapse: (cell: IMultiAnalysisTemplateCell) => void;
  faultFrequencies?: IFaultFrequencyChartCursor[] | null;
  trainRotationDirection?: TRAIN_ROTATION_DIRECTION | null;
}> = memo(
  ({
    cell,
    isActive,
    isExpanded,
    isCollapsed,
    canToggleExpand,
    canToggleCollapse,
    hasSelectedPoints,
    data,
    isLoading,
    displayCascade3D,
    fillAvailableHeight,
    toolbarState,
    setToolbarState,
    onSelect,
    onToggleExpand,
    onToggleCollapse,
    faultFrequencies = null,
    trainRotationDirection = null,
  }) => {
    const { formatMessage } = useIntl();
    const labelKey = MEASURE_WINDOW_LABEL_KEYS[cell.measureType];
    const resetZoomRef = useRef<(() => void) | null>(null);
    const [isZoomed, setIsZoomed] = useState(false);
    const [headerTooltipText, setHeaderTooltipText] = useState<string | null>(null);
    const [syncCursorDatasetIndex, setSyncCursorDatasetIndex] = useState<number | null>(null);
    const [syncMarkerDatasetIndex, setSyncMarkerDatasetIndex] = useState<number | null>(null);

    const handleRegisterReset = useCallback((handler: (() => void) | null) => {
      resetZoomRef.current = handler;
    }, []);

    const handleResetZoom = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      resetZoomRef.current?.();
    }, []);

    const handleSelectCell = useCallback(() => {
      onSelect(cell);
    }, [cell, onSelect]);

    const handleToggleExpand = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onToggleExpand(cell);
      },
      [cell, onToggleExpand],
    );

    const handleToggleCollapse = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onToggleCollapse(cell);
      },
      [cell, onToggleCollapse],
    );

    const handleHeaderTooltipChange = useCallback((text: string | null) => {
      setHeaderTooltipText(text);
    }, []);

    const chartData = useMemo(() => {
      if (!data || data.kind !== "signal") {
        return data;
      }

      const isSignalMeasureType =
        cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ||
        cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM;

      if (!isActive || !isSignalMeasureType) {
        return data;
      }

      const filteredData =
        isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM
          ? filterSignalChartData(data.data, toolbarState.signalAppliedHarmonics, toolbarState.signalAppliedBandpassHz)
          : data.data;

      return {
        kind: "signal" as const,
        data: filteredData.map(series =>
          convertSignalChartUnits(series, toolbarState.signalDisplayUnits, unitCode =>
            formatMessage({ id: `enums.units.${unitCode}` }),
          ),
        ),
      };
    }, [
      cell.measureType,
      data,
      formatMessage,
      isActive,
      toolbarState.signalAppliedBandpassHz,
      toolbarState.signalAppliedHarmonics,
      toolbarState.signalDisplayUnits,
    ]);
    const signalTimeScaleDisplay = useMemo(() => {
      if (
        (!isActive && !toolbarState.syncCursors) ||
        cell.measureType !== MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM ||
        chartData?.kind !== "signal" ||
        !chartData.data[0]
      ) {
        return undefined;
      }

      const sharedRotationFrequencyRpm = findRotationFrequencyRpm(chartData.data);

      if (sharedRotationFrequencyRpm === null) {
        return undefined;
      }

      const targetUnitsCode = toolbarState.signalTimeScale === SIGNAL_TIME_SCALE.REVOLUTIONS ? 6 : 5;
      const sourceUnitsCode = chartData.data[0].unitsXCode;
      const factor =
        sourceUnitsCode === targetUnitsCode
          ? 1
          : targetUnitsCode === 6
            ? sharedRotationFrequencyRpm / 60_000
            : 60_000 / sharedRotationFrequencyRpm;

      return {
        factor,
        units: formatMessage({ id: `enums.units.${targetUnitsCode}` }),
        unitsCode: targetUnitsCode,
      };
    }, [cell.measureType, chartData, formatMessage, isActive, toolbarState.signalTimeScale, toolbarState.syncCursors]);
    const spectrumFrequencyScaleDisplay = useMemo(() => {
      if (
        (!isActive && !toolbarState.syncCursors) ||
        cell.measureType !== MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ||
        chartData?.kind !== "signal" ||
        !chartData.data[0]
      ) {
        return undefined;
      }

      const rotationFrequencyRpm = findRotationFrequencyRpm(chartData.data);

      if (rotationFrequencyRpm === null) {
        return undefined;
      }

      const baseFrequencyHz = rotationFrequencyRpm / 60;
      const sourceIsOrders = chartData.data[0].unitsXCode === 4;
      const targetIsOrders = toolbarState.spectrumFrequencyScale === SPECTRUM_FREQUENCY_SCALE.ORDERS;
      const unitsCode = targetIsOrders ? 4 : 3;

      return {
        factor: sourceIsOrders === targetIsOrders ? 1 : targetIsOrders ? 1 / baseFrequencyHz : baseFrequencyHz,
        units: formatMessage({ id: `enums.units.${unitsCode}` }),
        unitsCode,
      };
    }, [
      cell.measureType,
      chartData,
      formatMessage,
      isActive,
      toolbarState.spectrumFrequencyScale,
      toolbarState.syncCursors,
    ]);

    const isFrequencyProfileEnabled =
      isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM && toolbarState.frequencyProfile;

    const handleFrequencyProfileClick = useCallback(
      (nativeX: number, datasetIndex: number) => {
        if (chartData?.kind !== "signal") {
          return;
        }

        const series = chartData.data[datasetIndex] ?? chartData.data[0];
        const frequencyHz = series
          ? convertClickedSpectrumXToHz(nativeX, series.unitsXCode, series.rotationFrequencyRpm)
          : null;

        if (frequencyHz == null) {
          return;
        }

        setToolbarState({ frequencyProfileSelection: { frequencyHz, nativeX } });
      },
      [chartData, setToolbarState],
    );

    const frequencyProfileNativeX = isFrequencyProfileEnabled
      ? toolbarState.frequencyProfileSelection?.nativeX ?? null
      : null;

    const syncMeasureType = isSyncableMeasureType(cell.measureType) ? cell.measureType : null;
    const isCursorSyncEnabled = toolbarState.syncCursors && syncMeasureType !== null;
    const syncCursorValue =
      syncMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM
        ? toolbarState.syncSpectrumCursor
        : syncMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM
          ? toolbarState.syncSignalCursor
          : null;
    const syncCursorValues = useMemo(
      () =>
        isCursorSyncEnabled && syncMeasureType && syncCursorValue?.locked && chartData?.kind === "signal"
          ? resolveSyncNativeValues(syncCursorValue.physicalX, syncMeasureType, chartData.data)
          : null,
      [isCursorSyncEnabled, syncMeasureType, syncCursorValue?.locked, syncCursorValue?.physicalX, chartData],
    );
    const syncMarkerValues = useMemo(
      () =>
        isCursorSyncEnabled && syncMeasureType && toolbarState.markerMode && chartData?.kind === "signal"
          ? resolveSyncNativeValues(syncCursorValue?.markerPhysicalX, syncMeasureType, chartData.data)
          : null,
      [isCursorSyncEnabled, syncMeasureType, toolbarState.markerMode, syncCursorValue?.markerPhysicalX, chartData],
    );

    const handleSyncedCursorAction = useCallback(
      (action: ISyncedCursorAction) => {
        if (!isCursorSyncEnabled || !syncMeasureType || chartData?.kind !== "signal") {
          return;
        }

        setSyncCursorDatasetIndex(action.datasetIndex);
        setSyncMarkerDatasetIndex(action.markerDatasetIndex);

        const series = chartData.data[action.datasetIndex] ?? chartData.data[0];
        const markerSeries = chartData.data[action.markerDatasetIndex ?? action.datasetIndex] ?? series;
        const field =
          syncMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ? "syncSpectrumCursor" : "syncSignalCursor";

        if (!action.locked && action.markerNativeX == null) {
          setToolbarState({ [field]: null });
          return;
        }

        const physicalX =
          action.nativeX != null
            ? convertNativeXToPhysicalX(
                action.nativeX,
                syncMeasureType,
                series?.unitsXCode,
                series?.rotationFrequencyRpm,
              )
            : null;
        const markerPhysicalX =
          action.markerNativeX != null
            ? convertNativeXToPhysicalX(
                action.markerNativeX,
                syncMeasureType,
                markerSeries?.unitsXCode,
                markerSeries?.rotationFrequencyRpm,
              )
            : null;

        if (physicalX == null && markerPhysicalX == null) {
          return;
        }

        setToolbarState({
          [field]: {
            physicalX: physicalX ?? markerPhysicalX ?? 0,
            markerPhysicalX,
            locked: action.locked,
          },
        });
      },
      [chartData, isCursorSyncEnabled, setToolbarState, syncMeasureType],
    );

    const orbitExternalControls = useMemo(
      () =>
        data?.kind === "orbit"
          ? {
              hideInlineControls: true,
              rotationDirection: isActive ? mapToolbarRotationDirection(toolbarState.rotationDirection) : undefined,
              showOrbitPoints: isActive ? toolbarState.showOrbitPoints : undefined,
              eccentricityMode: isActive ? toolbarState.eccentricity : undefined,
              orbitFilterOpen: isActive ? toolbarState.orbitFilterOpen : undefined,
              radialClearance: toolbarState.orbitRadialClearance,
              appliedOrbitBins: toolbarState.orbitAppliedBins,
              appliedBandpassHz: toolbarState.orbitAppliedBandpassHz,
              onEccentricityModeChange: (enabled: boolean) =>
                setToolbarState({
                  eccentricity: enabled,
                  ...(enabled ? {} : { orbitRadialClearance: null }),
                }),
              onOrbitFilterOpenChange: (open: boolean) => setToolbarState({ orbitFilterOpen: open }),
              onRadialClearanceChange: (value: number | null) => setToolbarState({ orbitRadialClearance: value }),
              onAppliedOrbitBinsChange: (bins: readonly number[]) => setToolbarState({ orbitAppliedBins: [...bins] }),
              onAppliedBandpassHzChange: (value: typeof toolbarState.orbitAppliedBandpassHz) =>
                setToolbarState({ orbitAppliedBandpassHz: value }),
            }
          : undefined,
      [
        data?.kind,
        isActive,
        setToolbarState,
        toolbarState.eccentricity,
        toolbarState.orbitAppliedBandpassHz,
        toolbarState.orbitAppliedBins,
        toolbarState.orbitFilterOpen,
        toolbarState.orbitRadialClearance,
        toolbarState.rotationDirection,
        toolbarState.showOrbitPoints,
      ],
    );

    useEffect(() => {
      setIsZoomed(false);
      resetZoomRef.current = null;
      setHeaderTooltipText(null);
    }, [cell.id]);

    const expandToggleLabel = formatMessage({
      id: isExpanded
        ? "pages.multiAnalysis.graphicArea.collapseWindow"
        : "pages.multiAnalysis.graphicArea.expandWindow",
    });
    const collapseToggleLabel = formatMessage({
      id: isCollapsed
        ? "pages.multiAnalysis.graphicArea.restoreWindow"
        : "pages.multiAnalysis.graphicArea.minimizeWindow",
    });

    const renderChart = () => {
      if (!hasSelectedPoints) {
        return (
          <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", px: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "#8b93a7", textAlign: "center" }}>
              <FormattedMessage id="pages.multiAnalysis.chartPlaceholder" />
            </Typography>
          </Stack>
        );
      }

      if (!data) {
        return (
          <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", gap: 0.75, px: 1.5 }}>
            <ShowChart sx={{ color: "#a2aabd" }} />
            <Typography sx={{ fontSize: 12, color: "#8b93a7", textAlign: "center" }}>
              <FormattedMessage id="pages.scheme.analytics.noChartData" />
            </Typography>
          </Stack>
        );
      }

      if (chartData?.kind === "signal") {
        return (
          <MultilineChartSignal
            data={chartData.data}
            chartTitle=""
            enableKeyboardCursorNavigation={isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM}
            markerMode={
              (isActive || isCursorSyncEnabled) && isSyncableMeasureType(cell.measureType) && toolbarState.markerMode
            }
            frequencyProfileNativeX={frequencyProfileNativeX}
            onNativeXClick={isFrequencyProfileEnabled ? handleFrequencyProfileClick : undefined}
            cursorLineColor={isCursorSyncEnabled ? SYNC_CURSOR_LINE_COLOR : undefined}
            syncCursorValues={syncCursorValues}
            syncMarkerValues={syncMarkerValues}
            syncCursorDatasetIndex={syncCursorDatasetIndex}
            syncMarkerDatasetIndex={syncMarkerDatasetIndex}
            onSyncedCursorAction={isCursorSyncEnabled ? handleSyncedCursorAction : undefined}
            showMarkerPower={cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM}
            markerTooltipType={cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ? "spectrum" : "signal"}
            tooltipInHeader
            onHeaderTooltipChange={handleHeaderTooltipChange}
            showHarmonicCursors={
              isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM && toolbarState.harmonicCursors
            }
            showSubHarmonicCursors={
              isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM && toolbarState.subHarmonicCursors
            }
            faultFrequencies={
              isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM ? faultFrequencies : null
            }
            showMarkerPoints={
              isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM && toolbarState.showOrbitPoints
            }
            displayCascade3D={displayCascade3D}
            showCascadeHoverLine={displayCascade3D}
            xAxisDisplayScale={signalTimeScaleDisplay?.factor ?? spectrumFrequencyScaleDisplay?.factor}
            xAxisDisplayUnits={signalTimeScaleDisplay?.units ?? spectrumFrequencyScaleDisplay?.units}
            xAxisDisplayUnitsCode={signalTimeScaleDisplay?.unitsCode ?? spectrumFrequencyScaleDisplay?.unitsCode}
            registerReset={handleRegisterReset}
            onZoomStateChange={setIsZoomed}
          />
        );
      }

      return (
        <MultilineChartOrbit
          key={cell.id}
          data={data.kind === "orbit" || data.kind === "polar" ? data.data : []}
          chartTitle=""
          isPolarTrendChecked={data.kind === "orbit" || data.kind === "polar"}
          polarTrendMode={data.kind === "polar"}
          trainRotationDirection={trainRotationDirection}
          registerReset={handleRegisterReset}
          onZoomStateChange={setIsZoomed}
          externalControls={orbitExternalControls}
        />
      );
    };

    return (
      <>
        {isActive && cell.measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM && data?.kind === "signal" ? (
          <SignalFilterDialog
            open={toolbarState.orbitFilterOpen}
            data={data.data}
            appliedHarmonics={toolbarState.signalAppliedHarmonics}
            appliedBandpassHz={toolbarState.signalAppliedBandpassHz}
            onClose={() => setToolbarState({ orbitFilterOpen: false })}
            onApply={(signalAppliedHarmonics, signalAppliedBandpassHz) =>
              setToolbarState({
                signalAppliedHarmonics: [...signalAppliedHarmonics],
                signalAppliedBandpassHz,
                orbitFilterOpen: false,
              })
            }
            onClear={() =>
              setToolbarState({
                signalAppliedHarmonics: [],
                signalAppliedBandpassHz: null,
              })
            }
          />
        ) : null}
        <Box
          sx={{
            ...(fillAvailableHeight || isExpanded || isCollapsed ? { height: "100%" } : {}),
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: isActive ? "2px solid #3f6db5" : GRAPHIC_AREA_BORDER,
            bgcolor: "#fff",
            overflow: "hidden",
            outline: "none",
            boxShadow: isActive ? "0 0 0 1px rgba(63, 109, 181, 0.2)" : "none",
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            onClick={handleSelectCell}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleSelectCell();
              }
            }}
            sx={{
              px: 1,
              py: 0.75,
              borderBottom: GRAPHIC_AREA_BORDER,
              bgcolor: isActive ? "#f0f4fc" : "#fff",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minHeight: 32,
              minWidth: 0,
              overflow: "hidden",
              cursor: "pointer",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: isActive ? "#3f6db5" : "#4f5d75",
                flexShrink: isCollapsed ? 1 : 0,
                minWidth: 0,
              }}
              noWrap
            >
              <FormattedMessage id={`pages.multiAnalysis.measureWindow.${labelKey}`} />
            </Typography>
            {headerTooltipText ? (
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#2e3445",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={headerTooltipText}
              >
                {headerTooltipText}
              </Typography>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}
            {canToggleCollapse ? (
              <Tooltip title={collapseToggleLabel}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={collapseToggleLabel}
                    onClick={handleToggleCollapse}
                    sx={{ flexShrink: 0, width: 24, height: 24, p: 0.25, color: "#4f5d75" }}
                  >
                    {isCollapsed ? <UnfoldMore sx={{ fontSize: 16 }} /> : <Minimize sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {!isCollapsed && canToggleExpand ? (
              <Tooltip title={expandToggleLabel}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={expandToggleLabel}
                    onClick={handleToggleExpand}
                    sx={{ flexShrink: 0, width: 24, height: 24, p: 0.25, color: "#4f5d75" }}
                  >
                    {isExpanded ? <CloseFullscreen sx={{ fontSize: 16 }} /> : <OpenInFull sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {isZoomed ? (
              <Tooltip title={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}
                    onClick={handleResetZoom}
                    sx={{ flexShrink: 0, width: 24, height: 24, p: 0.25, color: "#4f5d75" }}
                  >
                    <RestartAlt sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
          {!isCollapsed ? (
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
              }}
              onClick={handleSelectCell}
            >
              <ProgressOverlay isLoading={isLoading} wrapperSx={{ height: "100%", minHeight: 0 }}>
                {renderChart()}
              </ProgressOverlay>
            </Box>
          ) : null}
        </Box>
      </>
    );
  },
);

MeasureWindowCell.displayName = "MeasureWindowCell";

const TemplateEditCell: FC<{
  cell: IMultiAnalysisTemplateCell;
  configureFiltersManually: boolean;
  points: ReturnType<typeof useMultiAnalysis>["selectedPoints"];
  range: IMultiAnalysisTimeRange;
  onChange: (patch: Partial<Pick<IMultiAnalysisTemplateCell, "measureType" | "filters">>) => void;
}> = ({ cell, configureFiltersManually, points, range, onChange }) => (
  <Box
    sx={{
      minHeight: 150,
      display: "flex",
      flexDirection: "column",
      border: GRAPHIC_AREA_BORDER,
      bgcolor: "#fff",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 1, py: 0.75, borderBottom: GRAPHIC_AREA_BORDER, bgcolor: "#f0f4fc" }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#3f6db5" }} noWrap>
        <FormattedMessage
          id="pages.multiAnalysis.templateEditor.cellTitle"
          values={{ row: cell.row + 1, column: cell.column + 1 }}
        />
      </Typography>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1 }}>
      <TemplateCellEditor
        cell={cell}
        configureFiltersManually={configureFiltersManually}
        points={points}
        range={range}
        onChange={onChange}
      />
    </Box>
  </Box>
);

export const MultiAnalysisGraphicArea: FC<IMultiAnalysisGraphicAreaProps> = ({
  templates,
  isLoadingTemplates,
  trainName,
  range,
  appliedTemplateId,
  hiddenMeasureKeys,
  fillAvailableHeight = false,
  faultFrequencies = null,
  trainRotationDirection = null,
  onActiveSignalUnitsAvailabilityChange,
}) => {
  const user = useUser<IUserDto>();
  const {
    activeMeasureWindow,
    selectedPoints,
    selectedDataMarks,
    setActiveMeasureType,
    setActiveMeasureWindow,
    toolbarState,
    setToolbarState,
    getCellDisplayCascade3D,
    isTemplateEditMode,
    draftTemplate,
    updateDraftTemplateCell,
  } = useMultiAnalysis();
  const [expandedCellId, setExpandedCellId] = useState<string | null>(null);
  const [collapsedCellIds, setCollapsedCellIds] = useState<ReadonlySet<string>>(() => new Set());
  const previousActiveCellIdRef = useRef<string | null>(null);
  const pendingSignalUnitsSyncCellIdRef = useRef<string | null>(null);
  const selectedTemplate = useMemo(
    () =>
      isTemplateEditMode && draftTemplate
        ? draftTemplate
        : resolveAppliedAnalysisTemplate(templates, appliedTemplateId),
    [appliedTemplateId, draftTemplate, isTemplateEditMode, templates],
  );
  const visibleCells = useMemo(() => {
    const cells = selectedTemplate?.settings.cells ?? [];

    if (!expandedCellId) {
      return cells;
    }

    return cells.filter(cell => cell.id === expandedCellId);
  }, [expandedCellId, selectedTemplate?.settings.cells]);
  const canToggleExpand = (selectedTemplate?.settings.cells.length ?? 0) > 1;
  const canToggleCollapse = (selectedTemplate?.settings.cells.length ?? 0) > 1;
  const visibleRows = useMemo(() => {
    const rows = new Map<number, IMultiAnalysisTemplateCell[]>();

    visibleCells.forEach(cell => {
      const row = rows.get(cell.row);

      if (row) {
        row.push(cell);
      } else {
        rows.set(cell.row, [cell]);
      }
    });

    return [...rows.entries()]
      .sort(([leftRow], [rightRow]) => leftRow - rightRow)
      .map(([, cells]) => cells.sort((left, right) => left.column - right.column));
  }, [visibleCells]);
  const resolvedActiveMeasureWindow = useMemo(
    () => resolveActiveMeasureWindow(activeMeasureWindow, selectedTemplate),
    [activeMeasureWindow, selectedTemplate],
  );
  const { dataByCellId, isLoading: isLoadingGraphicAreaData } = useMultiAnalysisGraphicAreaData({
    enabled: Boolean(selectedTemplate) && !isTemplateEditMode,
    cells: selectedTemplate?.settings.cells ?? [],
    points: selectedPoints,
    range,
    selectedDataMarks,
  });
  const visibleDataByCellId = useMemo(
    () => filterGraphicAreaDataByHiddenMeasureKeys(dataByCellId, hiddenMeasureKeys),
    [dataByCellId, hiddenMeasureKeys],
  );
  const activeCellData = useMemo(() => {
    if (!resolvedActiveMeasureWindow) {
      return null;
    }

    return visibleDataByCellId[resolvedActiveMeasureWindow.cellId] ?? null;
  }, [resolvedActiveMeasureWindow, visibleDataByCellId]);
  const canConvertActiveSignalUnits = useMemo(
    () => (activeCellData?.kind === "signal" ? canConvertAnySignalChartUnits(activeCellData.data) : false),
    [activeCellData],
  );
  const configureTemplateFiltersManually = normalizeUserMultiAnalysisSettings(
    user.profile.interfaceSettings.multiAnalysis,
  ).configureTemplateFiltersManually;

  useEffect(() => {
    onActiveSignalUnitsAvailabilityChange?.(canConvertActiveSignalUnits);
  }, [canConvertActiveSignalUnits, onActiveSignalUnitsAvailabilityChange]);

  useEffect(() => {
    const activeCellId = resolvedActiveMeasureWindow?.cellId ?? null;

    if (previousActiveCellIdRef.current !== activeCellId) {
      previousActiveCellIdRef.current = activeCellId;
      pendingSignalUnitsSyncCellIdRef.current = activeCellId;
    }

    if (pendingSignalUnitsSyncCellIdRef.current !== activeCellId || activeCellData?.kind !== "signal") {
      return;
    }

    const nativeUnits = resolveNativeSignalDisplayUnit(activeCellData.data);

    if (nativeUnits !== null) {
      setToolbarState({ signalDisplayUnits: nativeUnits });
      pendingSignalUnitsSyncCellIdRef.current = null;
    }
  }, [activeCellData, resolvedActiveMeasureWindow?.cellId, setToolbarState]);

  useEffect(() => {
    if (isTemplateEditMode) {
      setActiveMeasureWindow(null);
      setActiveMeasureType(null);
      setExpandedCellId(null);
      setCollapsedCellIds(new Set());
      return;
    }

    if (!selectedTemplate && !isLoadingTemplates) {
      setActiveMeasureWindow(null);
      setActiveMeasureType(MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM);
      setExpandedCellId(null);
      setCollapsedCellIds(new Set());
      return;
    }

    if (
      expandedCellId &&
      selectedTemplate &&
      !selectedTemplate.settings.cells.some(cell => cell.id === expandedCellId)
    ) {
      setExpandedCellId(null);
    }

    if (selectedTemplate) {
      const availableCellIds = new Set(selectedTemplate.settings.cells.map(cell => cell.id));

      setCollapsedCellIds(previous => {
        const next = new Set([...previous].filter(cellId => availableCellIds.has(cellId)));

        return next.size === previous.size ? previous : next;
      });
    }

    if (!resolvedActiveMeasureWindow) {
      return;
    }

    if (
      activeMeasureWindow?.templateId !== resolvedActiveMeasureWindow.templateId ||
      activeMeasureWindow.cellId !== resolvedActiveMeasureWindow.cellId ||
      activeMeasureWindow.measureType !== resolvedActiveMeasureWindow.measureType
    ) {
      setActiveMeasureWindow(resolvedActiveMeasureWindow);
    }

    setActiveMeasureType(resolvedActiveMeasureWindow.measureType);
  }, [
    activeMeasureWindow,
    expandedCellId,
    isLoadingTemplates,
    isTemplateEditMode,
    resolvedActiveMeasureWindow,
    selectedTemplate,
    setActiveMeasureType,
    setActiveMeasureWindow,
  ]);

  useEffect(() => {
    setExpandedCellId(null);
    setCollapsedCellIds(new Set());
  }, [selectedTemplate?.idAnalysisTemplate]);

  const handleSelectCell = useCallback(
    (cell: IMultiAnalysisTemplateCell) => {
      if (!selectedTemplate) {
        return;
      }

      setActiveMeasureWindow({
        templateId: selectedTemplate.idAnalysisTemplate,
        cellId: cell.id,
        row: cell.row,
        column: cell.column,
        measureType: cell.measureType,
      });
      startTransition(() => {
        setActiveMeasureType(cell.measureType);
      });
    },
    [selectedTemplate, setActiveMeasureType, setActiveMeasureWindow],
  );

  const handleToggleExpand = useCallback(
    (cell: IMultiAnalysisTemplateCell) => {
      setCollapsedCellIds(previous => {
        if (!previous.has(cell.id)) {
          return previous;
        }

        const next = new Set(previous);
        next.delete(cell.id);
        return next;
      });
      setExpandedCellId(previous => (previous === cell.id ? null : cell.id));
      handleSelectCell(cell);
    },
    [handleSelectCell],
  );

  const handleToggleCollapse = useCallback((cell: IMultiAnalysisTemplateCell) => {
    setExpandedCellId(previous => (previous === cell.id ? null : previous));
    setCollapsedCellIds(previous => {
      const next = new Set(previous);

      if (next.has(cell.id)) {
        next.delete(cell.id);
      } else {
        next.add(cell.id);
      }

      return next;
    });
  }, []);

  if (!selectedTemplate && !isLoadingTemplates) {
    return (
      <Stack sx={{ height: "100%", minHeight: 0, alignItems: "center", justifyContent: "center", gap: 1.5, p: 2 }}>
        <GridView sx={{ color: "#8b93a7" }} />
        <Typography sx={{ fontSize: 13, color: "#6a7284", textAlign: "center" }}>
          <FormattedMessage id="pages.multiAnalysis.graphicArea.noTemplates" />
        </Typography>
      </Stack>
    );
  }

  return (
    <ProgressOverlay isLoading={isLoadingTemplates} wrapperSx={{ height: "100%", minHeight: 0 }}>
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            minHeight: GRAPHIC_AREA_HEADER_MIN_HEIGHT,
            px: 1.5,
            py: 1,
            borderBottom: GRAPHIC_AREA_BORDER,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#202534" }}>
            {selectedTemplate?.name || <FormattedMessage id="pages.multiAnalysis.template.defaultName" />}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6a7284" }}>{trainName}</Typography>
          <Typography sx={{ fontSize: 12, color: "#8b93a7", alignSelf: "center" }}>
            {isTemplateEditMode ? (
              <FormattedMessage id="pages.multiAnalysis.templateEditor.editingMode" />
            ) : (
              <FormattedMessage
                id="pages.multiAnalysis.graphicArea.selectedPoints"
                values={{ count: selectedPoints.length }}
              />
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "auto",
            bgcolor: "#f8f9fd",
          }}
        >
          <Box
            sx={{
              minHeight: fillAvailableHeight || expandedCellId ? "100%" : "auto",
              display: "flex",
              flexDirection: "column",
              gap: `${GRAPHIC_AREA_GRID_GAP}px`,
              p: `${GRAPHIC_AREA_GRID_PADDING}px`,
            }}
          >
            {isTemplateEditMode
              ? visibleRows.map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: `repeat(${selectedTemplate?.settings.layout.columns ?? 1}, minmax(0, 1fr))`,
                      },
                      gridAutoRows: {
                        xs: `${GRAPHIC_AREA_CELL_MIN_HEIGHT}px`,
                        md: `minmax(${GRAPHIC_AREA_CELL_MIN_HEIGHT}px, 1fr)`,
                      },
                      gap: `${GRAPHIC_AREA_GRID_GAP}px`,
                      flex: fillAvailableHeight ? "1 0 0" : "0 0 auto",
                      minHeight: {
                        xs:
                          row.length * GRAPHIC_AREA_CELL_MIN_HEIGHT +
                          Math.max(0, row.length - 1) * GRAPHIC_AREA_GRID_GAP,
                        md: GRAPHIC_AREA_CELL_MIN_HEIGHT,
                      },
                    }}
                  >
                    {row.map(cell => (
                      <TemplateEditCell
                        key={cell.id}
                        cell={cell}
                        configureFiltersManually={configureTemplateFiltersManually}
                        points={selectedPoints}
                        range={range}
                        onChange={patch => updateDraftTemplateCell(cell.id, patch)}
                      />
                    ))}
                  </Box>
                ))
              : visibleRows.map((row, rowIndex) => {
                  const isRowCollapsed = row.every(cell => collapsedCellIds.has(cell.id));
                  const expandedCellCount = row.filter(cell => !collapsedCellIds.has(cell.id)).length;
                  const mobileRowMinHeight =
                    expandedCellCount * GRAPHIC_AREA_CELL_MIN_HEIGHT +
                    (row.length - expandedCellCount) * GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT +
                    Math.max(0, row.length - 1) * GRAPHIC_AREA_GRID_GAP;

                  return (
                    <Box
                      key={rowIndex}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: `${GRAPHIC_AREA_GRID_GAP}px`,
                        flex: isRowCollapsed
                          ? "0 0 auto"
                          : fillAvailableHeight || expandedCellId
                            ? "1 0 0"
                            : "0 0 auto",
                        height: isRowCollapsed ? GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT : "auto",
                        minHeight: {
                          xs: isRowCollapsed ? GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT : mobileRowMinHeight,
                          md: isRowCollapsed ? GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT : GRAPHIC_AREA_CELL_MIN_HEIGHT,
                        },
                        transition: "flex 0.3s ease, height 0.3s ease",
                      }}
                    >
                      {row.map(cell => {
                        const isCollapsed = collapsedCellIds.has(cell.id);

                        return (
                          <Box
                            key={cell.id}
                            sx={{
                              minWidth: 0,
                              width: {
                                xs: "100%",
                                md: isCollapsed && !isRowCollapsed ? GRAPHIC_AREA_CELL_COLLAPSED_WIDTH : "auto",
                              },
                              height: {
                                xs: isCollapsed ? GRAPHIC_AREA_CELL_COLLAPSED_HEIGHT : GRAPHIC_AREA_CELL_MIN_HEIGHT,
                                md: "100%",
                              },
                              flex: isCollapsed && !isRowCollapsed ? "0 0 auto" : "1 1 0",
                              transition: "flex 0.3s ease, width 0.3s ease, height 0.3s ease",
                            }}
                          >
                            <MeasureWindowCell
                              cell={cell}
                              fillAvailableHeight
                              isActive={
                                resolvedActiveMeasureWindow?.templateId === selectedTemplate?.idAnalysisTemplate &&
                                resolvedActiveMeasureWindow?.cellId === cell.id
                              }
                              isExpanded={expandedCellId === cell.id}
                              isCollapsed={isCollapsed}
                              canToggleExpand={canToggleExpand}
                              canToggleCollapse={canToggleCollapse}
                              hasSelectedPoints={selectedPoints.length > 0}
                              data={visibleDataByCellId[cell.id]}
                              isLoading={isLoadingGraphicAreaData}
                              displayCascade3D={
                                selectedTemplate
                                  ? getCellDisplayCascade3D(selectedTemplate.idAnalysisTemplate, cell.id)
                                  : true
                              }
                              toolbarState={toolbarState}
                              setToolbarState={setToolbarState}
                              onSelect={handleSelectCell}
                              onToggleExpand={handleToggleExpand}
                              onToggleCollapse={handleToggleCollapse}
                              faultFrequencies={faultFrequencies}
                              trainRotationDirection={trainRotationDirection}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      </Box>
    </ProgressOverlay>
  );
};

interface IMultiAnalysisGraphicAreaPanelProps extends IMultiAnalysisGraphicAreaProps {
  sx?: SxProps<Theme>;
}

export const MultiAnalysisGraphicAreaPanel: FC<IMultiAnalysisGraphicAreaPanelProps> = ({ sx, ...graphicAreaProps }) => (
  <MultiAnalysisPanel
    hideHeader
    sx={[
      {
        flex: "1 1 auto",
        minHeight: 0,
        height: "100%",
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <MultiAnalysisGraphicArea {...graphicAreaProps} />
  </MultiAnalysisPanel>
);
